'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { Button } from '@extern/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import { SignatureIcon, UserCirclePlusIcon } from '@phosphor-icons/react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useUtmParams } from '@/hooks/use-utm-params';
import { SegmentProvider } from '@/lib/analytics/segment';
import { useGiftMutation } from '../_api/use-gift';
import { type GiftFormData, giftFormSchema } from '../_utils/schema';
import { OrderSummary } from './order-summary';
import { RecipientForm } from './recipient-form';
export function GiftForm() {
  const $ = _c(54);
  useUtmParams();
  const giftMutation = useGiftMutation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new SegmentProvider();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const segment = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      resolver: zodResolver(giftFormSchema),
      defaultValues: {
        recipients: [{
          firstName: "",
          lastName: "",
          email: "",
          quantity: 3,
          type: "SUBSCRIPTION",
          message: ""
        }],
        sender: {
          firstName: "",
          lastName: "",
          email: "",
          anonymousId: null
        }
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const form = useForm(t1);
  let t2;
  if ($[2] !== form.control) {
    t2 = {
      control: form.control,
      name: "recipients"
    };
    $[2] = form.control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t2);
  let t3;
  if ($[4] !== append || $[5] !== fields.length) {
    t3 = () => {
      if (fields.length >= 10) {
        toast.error("Maximum of 10 recipients allowed");
        return;
      }
      append({
        firstName: "",
        lastName: "",
        email: "",
        quantity: 3,
        type: "SUBSCRIPTION",
        message: ""
      });
    };
    $[4] = append;
    $[5] = fields.length;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const addRecipient = t3;
  let t4;
  if ($[7] !== giftMutation) {
    t4 = data => {
      giftMutation.mutate(data, {
        onSuccess: _temp,
        onError: _temp2
      });
    };
    $[7] = giftMutation;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onSubmit = t4;
  let t5;
  if ($[9] !== form) {
    t5 = () => {
      const id = segment.anonymousId();
      if (id) {
        form.setValue("sender.anonymousId", id);
      }
    };
    $[9] = form;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== form.setValue) {
    t6 = [segment, form.setValue];
    $[11] = form.setValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== form || $[14] !== onSubmit) {
    t7 = form.handleSubmit(onSubmit);
    $[13] = form;
    $[14] = onSubmit;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-full bg-role-info-container text-role-on-info-container"><UserCirclePlusIcon className="h-5 w-5" /></div><Text className="font-medium text-[22px] text-role-on-background leading-[28px]">Who are you gifting to?</Text></div>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== fields || $[18] !== form.control || $[19] !== remove) {
    let t10;
    if ($[21] !== fields.length || $[22] !== form.control || $[23] !== remove) {
      t10 = (field, index) => <RecipientForm canRemove={fields.length > 1} control={form.control} index={index} key={field.id} onRemove={remove} recipientNumber={index + 1} />;
      $[21] = fields.length;
      $[22] = form.control;
      $[23] = remove;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    t9 = fields.map(t10);
    $[17] = fields;
    $[18] = form.control;
    $[19] = remove;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[25] !== t9) {
    t10 = <div className="space-y-6">{t9}</div>;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = fields.length >= 10;
  let t12;
  if ($[27] !== addRecipient || $[28] !== t11) {
    t12 = <Button appearance="tonal" className="font-medium text-[16px] text-role-on-surface-container" disabled={t11} onClick={addRecipient} rounded="xl" size="sm" type="button">Add another recipient</Button>;
    $[27] = addRecipient;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Text className="text-[12px] text-role-on-background-subtle">Maximum of 10 recipients</Text>;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t12) {
    t14 = <div className="flex flex-col items-center gap-6">{t12}{t13}</div>;
    $[31] = t12;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t10 || $[34] !== t14) {
    t15 = <div className="space-y-6 rounded-3xl border border-role-outline-subtle bg-role-surface p-6">{t8}{t10}{t14}</div>;
    $[33] = t10;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-full bg-role-info-container text-role-on-info-container"><SignatureIcon className="h-5 w-5" /></div><Text className="font-medium text-[22px] text-role-on-background leading-[28px]">Sign your gift</Text></div>;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== form.control) {
    t17 = <div className="space-y-6 rounded-3xl border border-role-outline-subtle bg-role-surface p-6">{t16}<div className="grid grid-cols-1 gap-6 md:grid-cols-2"><FormField control={form.control} name="sender.firstName" render={_temp3} /><FormField control={form.control} name="sender.lastName" render={_temp4} /></div><FormField control={form.control} name="sender.email" render={_temp5} /></div>;
    $[37] = form.control;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15 || $[40] !== t17) {
    t18 = <div className="col-span-1 space-y-6 md:col-span-2">{t15}{t17}</div>;
    $[39] = t15;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== form.control || $[43] !== giftMutation.isPending) {
    t19 = <div className="col-span-1"><OrderSummary control={form.control} isLoading={giftMutation.isPending} /></div>;
    $[42] = form.control;
    $[43] = giftMutation.isPending;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t18 || $[46] !== t19) {
    t20 = <div className="grid grid-cols-1 gap-10 md:grid-cols-3">{t18}{t19}</div>;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t20 || $[49] !== t7) {
    t21 = <form onSubmit={t7}>{t20}</form>;
    $[48] = t20;
    $[49] = t7;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== form || $[52] !== t21) {
    t22 = <Form {...form}>{t21}</Form>;
    $[51] = form;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  return t22;
}
function _temp5(t0) {
  const {
    field: field_2
  } = t0;
  return <FormItem><FormLabel className="relative text-[14px] text-role-on-background leading-[20px]"><span className="absolute -top-0.5 -left-2 text-role-error">*</span>Email</FormLabel><FormControl><Input placeholder="Enter your email address" type="email" {...field_2} /></FormControl><FormMessage /></FormItem>;
}
function _temp4(t0) {
  const {
    field: field_1
  } = t0;
  return <FormItem><FormLabel className="relative text-[14px] text-role-on-background leading-[20px]"><span className="absolute -top-0.5 -left-2 text-role-error">*</span>Last name</FormLabel><FormControl><Input placeholder="Enter your last name" {...field_1} /></FormControl><FormMessage /></FormItem>;
}
function _temp3(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel className="relative text-[14px] text-role-on-background leading-[20px]"><span className="absolute -top-0.5 -left-2 text-role-error">*</span>First name</FormLabel><FormControl><Input placeholder="Enter your first name" {...field_0} /></FormControl><FormMessage /></FormItem>;
}
function _temp2(error) {
  toast.error("Failed to process gift. Please try again.");
  console.error("Error submitting gift form:", error);
}
function _temp() {
  toast.success("Redirecting to checkout...");
}